import produce from 'immer';
import { Genre, Menu, MenuBookEditorRowDataType, SubGenre, SubMenu, SubMenuGroup } from './models';
import { MultiSelectToArg, MutilDragAwareArg, ReorderResult } from './types';

export function reorder(list: Array<any>, startIndex: number, endIndex: number) {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
}

const reorderSingleDrag = ({
  data,
  selectedItemIds,
  source,
  destination,
  type,
  droppableId,
}: MutilDragAwareArg): ReorderResult => {
  const [, genreId, subgenreId, menuId, subMenuGroupId, subMenuId, childSubMenuGroupId] = droppableId.split('-');
  const shallow = [...data];
  let reorderData = shallow;

  if (type === 'genre') {
    reorderData = reorder(shallow, source.index, destination.index);
  }

  if (type === 'subgenre') {
    reorderData = produce(shallow, (draft) => {
      const genre = draft.find((genre) => genre.id === Number(genreId));
      if (genre && destination) {
        const newSubGenres = reorder(genre.subgenres, source.index, destination.index);
        genre.subgenres = newSubGenres;
      }
    });
  }

  if (type === 'menu') {
    reorderData = produce(shallow, (draft) => {
      const genre = draft.find((genre) => genre.id === Number(genreId));
      if (genre && destination) {
        const subgenre = genre.subgenres.find((subgenre) => subgenre.id === Number(subgenreId));
        if (subgenre) {
          const newMenus = reorder(subgenre.menus, source.index, destination.index);
          subgenre.menus = newMenus;
        }
      }
    });
  }

  if (type === 'sub_menu_group') {
    reorderData = produce(shallow, (draft) => {
      const genre = draft.find((genre) => genre.id === Number(genreId));
      if (genre && destination) {
        const subgenre = genre.subgenres.find((subgenre) => subgenre.id === Number(subgenreId));
        if (subgenre) {
          const menu = subgenre.menus.find((menu) => menu.id === Number(menuId));
          if (menu) {
            const newSubMenuGroups = reorder(menu.sub_menu_groups, source.index, destination.index);
            menu.sub_menu_groups = newSubMenuGroups;
          }
        }
      }
    });
  }

  if (type === 'sub_menu') {
    reorderData = produce(shallow, (draft) => {
      const genre = draft.find((genre) => genre.id === Number(genreId));
      if (genre && destination) {
        const subgenre = genre.subgenres.find((subgenre) => subgenre.id === Number(subgenreId));
        if (subgenre) {
          const menu = subgenre.menus.find((menu) => menu.id === Number(menuId));
          if (menu) {
            const subMenuGroup = menu.sub_menu_groups.find(
              (subMenuGroup) => subMenuGroup.id === Number(subMenuGroupId)
            );

            if (subMenuGroup) {
              const newSubMenus = reorder(subMenuGroup.sub_menus, source.index, destination.index);
              subMenuGroup.sub_menus = newSubMenus;
            }
          }
        }
      }
    });
  }

  if (type === 'child_sub_menu_group') {
    reorderData = produce(shallow, (draft) => {
      const genre = draft.find((genre) => genre.id === Number(genreId));
      if (genre && destination) {
        const subgenre = genre.subgenres.find((subgenre) => subgenre.id === Number(subgenreId));
        if (subgenre) {
          const menu = subgenre.menus.find((menu) => menu.id === Number(menuId));
          if (menu) {
            const subMenuGroup = menu.sub_menu_groups.find(
              (subMenuGroup) => subMenuGroup.id === Number(subMenuGroupId)
            );
            if (subMenuGroup) {
              const subMenu = subMenuGroup.sub_menus.find((subMenu) => subMenu.id === Number(subMenuId));
              if (subMenu) {
                const newChildSubMenuGroups = reorder(subMenu.child_sub_menu_groups, source.index, destination.index);
                subMenu.child_sub_menu_groups = newChildSubMenuGroups;
              }
            }
          }
        }
      }
    });
  }

  if (type === 'child_sub_menu') {
    reorderData = produce(shallow, (draft) => {
      const genre = draft.find((genre) => genre.id === Number(genreId));
      if (genre && destination) {
        const subgenre = genre.subgenres.find((subgenre) => subgenre.id === Number(subgenreId));
        if (subgenre) {
          const menu = subgenre.menus.find((menu) => menu.id === Number(menuId));
          if (menu) {
            const subMenuGroup = menu.sub_menu_groups.find(
              (subMenuGroup) => subMenuGroup.id === Number(subMenuGroupId)
            );
            if (subMenuGroup) {
              const subMenu = subMenuGroup.sub_menus.find((subMenu) => subMenu.id === Number(subMenuId));
              if (subMenu) {
                const childSubMenuGroup = subMenu.child_sub_menu_groups.find(
                  (childSubMenuGroup) => childSubMenuGroup.id === Number(childSubMenuGroupId)
                );
                if (childSubMenuGroup) {
                  const newChildSubMenus = reorder(childSubMenuGroup.child_sub_menus, source.index, destination.index);
                  childSubMenuGroup.child_sub_menus = newChildSubMenus;
                }
              }
            }
          }
        }
      }
    });
  }

  return {
    data: reorderData,
    selectedItemIds,
  };
};

function getDataListByType(data: Genre[], type: string, droppableId: string): MenuBookEditorRowDataType[] {
  if (type === 'genre') {
    return data;
  }

  if (type === 'subgenre') {
    const [, genreId] = droppableId.split('-');

    const genre = data.find((genre) => genre.id === Number(genreId));
    if (genre) {
      return genre.subgenres;
    }
  }

  if (type === 'menu') {
    const [, genreId, subgenreId] = droppableId.split('-');

    const genre = data.find((genre) => genre.id === Number(genreId));
    if (genre) {
      const subgenre = genre.subgenres.find((subgenre) => subgenre.id === Number(subgenreId));
      if (subgenre) {
        return subgenre.menus;
      }
    }
  }

  if (type === 'sub_menu_group') {
    const [, genreId, subgenreId, menuId] = droppableId.split('-');

    const genre = data.find((genre) => genre.id === Number(genreId));
    if (genre) {
      const subgenre = genre.subgenres.find((subgenre) => subgenre.id === Number(subgenreId));
      if (subgenre) {
        const menu = subgenre.menus.find((menu) => menu.id === Number(menuId));
        if (menu) {
          return menu.sub_menu_groups;
        }
      }
    }
  }

  if (type === 'sub_menu') {
    const [, genreId, subgenreId, menuId, subMenuGroupId] = droppableId.split('-');

    const genre = data.find((genre) => genre.id === Number(genreId));
    if (genre) {
      const subgenre = genre.subgenres.find((subgenre) => subgenre.id === Number(subgenreId));
      if (subgenre) {
        const menu = subgenre.menus.find((menu) => menu.id === Number(menuId));
        if (menu) {
          const subMenuGroup = menu.sub_menu_groups.find((subMenuGroup) => subMenuGroup.id === Number(subMenuGroupId));

          if (subMenuGroup) {
            return subMenuGroup.sub_menus;
          }
        }
      }
    }
  }

  return [];
}

function getDataWithUpdatedItemByType(
  data: Genre[],
  type: string,
  updateData: MenuBookEditorRowDataType[],
  droppableId: string
) {
  if (type === 'genre') {
    return updateData as Genre[];
  }

  if (type === 'subgenre') {
    const [, genreId] = droppableId.split('-');

    return produce(data, (draft) => {
      const genre = draft.find((genre) => genre.id === Number(genreId));
      if (genre) {
        genre.subgenres = updateData as SubGenre[];
      }
    });
  }

  if (type === 'subgenre') {
    const [, genreId] = droppableId.split('-');

    return produce(data, (draft) => {
      const genre = draft.find((genre) => genre.id === Number(genreId));
      if (genre) {
        genre.subgenres = updateData as SubGenre[];
      }
    });
  }

  if (type === 'menu') {
    const [, genreId, subgenreId] = droppableId.split('-');

    return produce(data, (draft) => {
      const genre = draft.find((genre) => genre.id === Number(genreId));
      if (genre) {
        const subgenre = genre.subgenres.find((subgenre) => subgenre.id === Number(subgenreId));
        if (subgenre) {
          subgenre.menus = updateData as Menu[];
        }
      }
    });
  }

  if (type === 'sub_menu_group') {
    const [, genreId, subgenreId, menuId] = droppableId.split('-');

    return produce(data, (draft) => {
      const genre = draft.find((genre) => genre.id === Number(genreId));
      if (genre) {
        const subgenre = genre.subgenres.find((subgenre) => subgenre.id === Number(subgenreId));
        if (subgenre) {
          const menu = subgenre.menus.find((menu) => menu.id === Number(menuId));
          if (menu) {
            menu.sub_menu_groups = updateData as SubMenuGroup[];
          }
        }
      }
    });
  }

  if (type === 'sub_menu') {
    const [, genreId, subgenreId, menuId, subMenuGroupId] = droppableId.split('-');

    return produce(data, (draft) => {
      const genre = draft.find((genre) => genre.id === Number(genreId));
      if (genre) {
        const subgenre = genre.subgenres.find((subgenre) => subgenre.id === Number(subgenreId));
        if (subgenre) {
          const menu = subgenre.menus.find((menu) => menu.id === Number(menuId));
          if (menu) {
            const subMenuGroup = menu.sub_menu_groups.find(
              (subMenuGroup) => subMenuGroup.id === Number(subMenuGroupId)
            );

            if (subMenuGroup) {
              subMenuGroup.sub_menus = updateData as SubMenu[];
            }
          }
        }
      }
    });
  }

  return [];
}

const reorderMultiDrag = ({
  data,
  selectedItemIds,
  source,
  destination,
  type,
  droppableId,
}: MutilDragAwareArg): ReorderResult => {
  const sourceList = getDataListByType(data, type, droppableId);
  const dragged = sourceList[source.index].id;

  const insertAtIndex: number = (() => {
    const destinationIndexOffset: number = selectedItemIds.reduce((previous: number, current: number): number => {
      if (current === dragged) {
        return previous;
      }

      const index = sourceList.findIndex((item) => item.id === current);

      if (index >= destination.index) {
        return previous;
      }

      // the selected item is before the destination index
      // we need to account for this when inserting into the new location
      return previous + 1;
    }, 0);

    const result: number = destination.index - destinationIndexOffset;
    return result;
  })();

  // doing the ordering now as we are required to look up columns
  // and know original ordering
  const orderedSelectedItemIds = [...selectedItemIds];

  const remainings = sourceList.filter((item) => !selectedItemIds.includes(item.id));
  const base = [...remainings];
  const orderedSelectedItem = sourceList.filter((item) => selectedItemIds.includes(item.id));
  base.splice(insertAtIndex, 0, ...orderedSelectedItem);

  const updated = getDataWithUpdatedItemByType(data, type, base, droppableId);

  return {
    data: updated,
    selectedItemIds: orderedSelectedItemIds,
  };
};

export const mutliDragAwareReorder = (args: MutilDragAwareArg): ReorderResult => {
  if (args.selectedItemIds.length > 1) {
    return reorderMultiDrag(args);
  }

  return reorderSingleDrag(args);
};

export const multiSelectTo = (
  { data, selectedItemIds, type, droppableId }: MultiSelectToArg,
  newItemId: number
): number[] | null => {
  const sourceList = getDataListByType(data, type, droppableId);

  // Nothing already selected
  if (!selectedItemIds.length) {
    return [newItemId];
  }

  const indexOfNew = sourceList.findIndex((item) => item.id === newItemId);
  const lastSelected = selectedItemIds[selectedItemIds.length - 1];
  const indexOfLast = sourceList.findIndex((item) => item.id === lastSelected);

  // nothing to do here
  if (indexOfNew === indexOfLast) {
    return null;
  }

  const isSelectingForwards: boolean = indexOfNew > indexOfLast;
  const start: number = isSelectingForwards ? indexOfLast : indexOfNew;
  const end: number = isSelectingForwards ? indexOfNew : indexOfLast;

  const inBetweenItems = sourceList.slice(start, end + 1);
  const inBetween = inBetweenItems.map((item) => item.id);

  // everything inbetween needs to have it's selection toggled.
  // with the exception of the start and end values which will always be selected
  const toAdd = inBetween.filter((id: number): boolean => {
    // if already selected: then no need to select it again
    if (selectedItemIds.includes(id)) {
      return false;
    }
    return true;
  });

  const sorted = isSelectingForwards ? toAdd : [...toAdd].reverse();
  const combined = [...selectedItemIds, ...sorted];

  return combined;
};

// Determines if the platform specific toggle selection in group key was used
export function wasToggleInSelectionGroupKeyUsed(
  event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>
) {
  const platform = (navigator as any).userAgentData?.platform;
  const isUsingWindows = platform && (platform.indexOf('Win') >= 0 || platform.indexOf('Linux') >= 0);
  return isUsingWindows ? event.ctrlKey : event.metaKey;
}

// Determines if the multiSelect key was used
export function wasMultiSelectKeyUsed(event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) {
  return event.shiftKey;
}
